<template>
    <div class="mkt-reinsurance">
        <div v-for="(item, i) in items" :key="i" class="mkt-reinsurance__item">
            <ReassuranceIcon :icon="item.reassuranceIcon ?? undefined" />
            {{ item.reassuranceText }}
        </div>
        <div
            v-if="hasTrustpilot"
            ref="trustpilotRef"
            class="mkt-reinsurance__trustpilot"
            :data-locale="trustpilotDataLocale"
            :data-template-id="trustpilotDataTemplateId"
            :data-businessunit-id="trustpilotDataBusinessunitId"
            data-theme="light"
        >
            <PrismicLink :field="trustpilotLink as FilledLinkToWebField">Trustpilot</PrismicLink>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, onMounted, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledLinkToWebField, LinkField} from '@prismicio/types';
    import {PrismicLink} from '@prismicio/vue';
    import ReassuranceIcon from './ReassuranceIcon.vue';

    const props = defineProps<{
        items: Array<{reassuranceIcon?: string | null; reassuranceText?: string | null}>;
        trustpilotDataLocale: string;
        trustpilotDataTemplateId: string;
        trustpilotDataBusinessunitId: string;
        trustpilotLink: LinkField;
        hideTrustpilot: boolean;
    }>();

    const trustpilotRef = ref<HTMLDivElement>();

    const hasTrustpilot = computed(
        () =>
            !props.hideTrustpilot &&
            !!props.trustpilotDataLocale &&
            !!props.trustpilotDataTemplateId &&
            !!props.trustpilotDataBusinessunitId &&
            isFilled.link(props.trustpilotLink),
    );

    function initTrustpilot() {
        if (trustpilotRef.value) {
            // @ts-expect-error ts doesn't know about Trustpilot
            window.Trustpilot.loadFromElement(trustpilotRef.value);
        }
    }

    onMounted(() => {
        if (hasTrustpilot.value) {
            // @ts-expect-error ts doesn't know about Trustpilot
            if (window.Trustpilot) {
                initTrustpilot();
            } else {
                const script = document.createElement('script');
                script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
                document.head.appendChild(script);
                script.addEventListener('load', () => {
                    initTrustpilot();
                });
            }
        }
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .mkt-reinsurance {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        gap: var(--joy-core-spacing-6);
        height: unset;
        width: 100%;
        font-size: var(--joy-font-size-primary-300);
        user-select: none;

        @include mq.screen_sm(min) {
            flex-direction: row;
            align-items: center;
            gap: var(--joy-core-spacing-10);
        }

        &__item {
            display: flex;
            align-items: center;
            gap: 16px;
            height: 28px;
            color: var(--joy-color-neutral-40);

            svg {
                filter: grayscale(100%);
            }
        }

        &__trustpilot {
            height: 22px;
            margin: var(--joy-core-spacing-4) auto 0;

            @include mq.screen_sm(min) {
                margin: 0 0 0 auto;
                text-align: right;
            }

            iframe {
                max-width: 260px;
                max-height: 22px;
            }
        }
    }
</style>
